<template>
	<ArrowImageGallery
		v-if="images?.length > 1 && bigImageIndexRef"
		arrow-direction="left"
		:class="[`${showPopUp && 'ml-4'}`]"
		:number-of-slides="images?.length"
		class="lg:hidden"
		@click="decreaseBigImageIndex(bigImageIndexRef - 1)"
	/>
	<div
		ref="bigPictureCarouselRef"
		class="flex lg:overflow-hidden lg:flex-col overflow-x-auto no-scrollbar h-full items-center"
	>
		<div
			v-for="(image, index) in images"
			:id="`${!showPopUp && 'bigImagesGallery'}`"
			:key="imageId(image) + size"
			ref="bigImagesGallery"
			:class="[
				`flex ${size ? 'min-w-full' : 'min-w-[568px]'} relative max-h-full `
			]"
		>
			<div
				:ref="
					(el) => {
						imagesRef[index] = {
							[size]: el as HTMLElement
						};
					}
				"
				:class="[`${zoomedInPopUpPictureWrapper} w-[100%] justify-center `]"
			>
				<div
					:class="[
						` ${zoomedInPopUpPictureChild} flex min-w-[90%] h-full absolute left-[50%] -translate-x-[50%]`
					]"
					data-testid="product-gallery-zoom"
					@click="doAction"
				></div>
				<!-- Show big image on zoom.
				Keep `sizes` breakpoint-keyed: @nuxt/image keys an un-prefixed value
				to the "1px" screen, so a bare "100vw" yields a `1w`/`2w` srcset. Inert
				while this fork stays on the default provider (absolute Norce URLs are
				passed through untouched), but it bites the moment `provider="norce"`
				is ported over from layers/base. -->
				<NuxtImg
					v-if="showPopUp"
					:src="`${imagePath(image)}?format=webp`"
					alt="product image"
					:preload="index == 0"
					:loading="index != 0 ? 'lazy' : 'eager'"
					class="h-auto w-auto object-center aspect-[2/3]"
					sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
					:placeholder="
						img(`${imagePath(image)}?format=webp`, {
							h: 10,
							f: 'webp',
							blur: 2,
							q: 50
						})
					"
				/>
				<!-- Show small image otherwise -->
				<img
					v-else-if="isNorceCdnUrl(imagePath(image))"
					:src="appendParams(imagePath(image), 'format=webp')"
					:srcset="
						buildNorceSrcSet(appendParams(imagePath(image), 'format=webp'))
					"
					alt="product image"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:fetchpriority="index === 0 ? 'high' : 'auto'"
					class="block aspect-[2/3] object-cover w-full object-center"
					sizes="100vw"
				/>
				<NuxtImg
					v-else
					:src="appendParams(imagePath(image), 'format=webp&w=570')"
					alt="product image"
					:preload="index != 0 ? false : { fetchPriority: 'high' }"
					:loading="index != 0 ? 'lazy' : 'eager'"
					:fetchpriority="index === 0 ? 'high' : 'auto'"
					class="aspect-[2/3] object-cover w-full object-center"
					sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw"
					:placeholder="
						img(appendParams(imagePath(image), 'format=webp&w=570'), {
							h: 10,
							f: 'webp',
							blur: 2,
							q: 50
						})
					"
					width="364"
					height="546"
				/>
			</div>
		</div>
	</div>
	<ArrowImageGallery
		v-if="images?.length > 1 && bigImageIndexRef < images?.length - 1"
		:number-of-slides="images?.length"
		arrow-direction="right"
		:class="[`${showPopUp && 'mr-4'}`]"
		class="lg:hidden"
		@click="increaseBigImageIndex(bigImageIndexRef + 1)"
	/>
</template>

<script setup lang="ts">
import { toRef } from "vue";
import { useResizeObserver } from "@vueuse/core";
import type { ProductFile } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import type { CategoryFiles } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import type { FilteredProductItemFiles } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	SetShowPopUp,
	BigImageIndex
} from "~/types/storyblok/imageGallery/imageGallery";

const img = useImage();

interface Props {
	images: ProductFile[] | CategoryFiles[] | FilteredProductItemFiles[];
	bigImageIndex: number;
	decreaseBigImageIndex: BigImageIndex;
	increaseBigImageIndex: BigImageIndex;
	size: string;
	setShowPopUp?: SetShowPopUp;
	showPopUp?: boolean;
}

const {
	images,
	bigImageIndex,
	decreaseBigImageIndex,
	increaseBigImageIndex,
	size,
	setShowPopUp,
	showPopUp
} = defineProps<Props>();

const bigImageIndexRef = toRef(() => bigImageIndex);
const bigImagesGallery = ref<HTMLElement[]>();
const imagesRef = ref<{ [key: string]: HTMLElement }[]>([]);
const bigPictureCarouselRef = ref<HTMLElement>();

const zoomedInPopUpPictureWrapper = ref("flex");
const zoomedInPopUpPictureChild = ref("cursor-zoom-in");
const slideToImage = ({
	displayedBigImage,
	smooth
}: {
	displayedBigImage: number;
	smooth?: boolean;
}) => {
	if (Object.keys(imagesRef.value).length) {
		if (bigPictureCarouselRef.value) {
			const stepSize =
				imagesRef?.value?.[displayedBigImage]?.[size]?.getBoundingClientRect()
					?.width;
			if (smooth) {
				bigPictureCarouselRef.value.classList.add("scroll-smooth");
			}
			const scrollLeft = stepSize! * displayedBigImage;
			bigPictureCarouselRef.value.scrollLeft = scrollLeft;
		}
	}
};

onMounted(() => {
	slideToImage({ displayedBigImage: bigImageIndex });
});

const doAction = () => {
	if (!showPopUp) {
		if (setShowPopUp) {
			setShowPopUp(true);
		}
		zoomedInPopUpPictureWrapper.value = "";

		const body = document.getElementsByTagName("body")[0];
		if (body) {
			body.style.overflow = "hidden";
		}

		zoomedInPopUpPictureChild.value = "max-h-[2600px] cursor-zoom-out";
	} else {
		if (zoomedInPopUpPictureWrapper.value === "flex") {
			zoomedInPopUpPictureChild.value = "max-h-[2600px] cursor-zoom-out";

			return (zoomedInPopUpPictureWrapper.value = "");
		} else {
			zoomedInPopUpPictureWrapper.value = "flex";
			zoomedInPopUpPictureChild.value = "cursor-zoom-in";
		}
	}
};

const emits = defineEmits(["bigImagesGalleryRef"]);
watchEffect(() => {
	slideToImage({
		displayedBigImage: bigImageIndex,
		smooth: true
	});

	emits("bigImagesGalleryRef", { bigImagesGallery });
});

useResizeObserver(bigPictureCarouselRef, () => {
	slideToImage({
		displayedBigImage: bigImageIndex,
		smooth: true
	});
});

const { isCategoryFile, isFilteredProductItemFiles } = useNorcePwa();

const imagePath = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) ? image?.ImagePath : image?.Path;
};

const imageId = (
	image: ProductFile | CategoryFiles | FilteredProductItemFiles
) => {
	return isCategoryFile(image) || isFilteredProductItemFiles(image)
		? image?.ImageKey
		: image?.Key;
};

const appendParams = (src: string, params: string) => {
	const separator = src.includes("?") ? "&" : "?";
	return `${src}${separator}${params}`;
};

const isNorceCdnUrl = (src: string) => src.includes("cdn-norce.tech");

const buildNorceSrcSet = (src: string) => {
	if (!isNorceCdnUrl(src)) return "";
	const widths = [640, 960, 1200, 1600, 1920, 2560, 3200];
	return widths.map((w) => `${appendParams(src, `w=${w}`)} ${w}w`).join(", ");
};
</script>
